import type { ChildTextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { hideInLineTrackTip } from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import type { DataNode } from 'components/WorkspaceEditor/types'
import {
    getStatusClass,
    normalizeTextContent,
} from 'components/WorkspaceEditor/utils'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { PlainText } from './PlainText/PlainText'
import { usePlainTextStyles } from './PlainText/usePlainTextStyles'

interface EmptyPlainTextContainerProps {
    element: Pick<ChildTextContentElement, 'id'> &
        Partial<Pick<ChildTextContentElement, 'changeType' | 'text' | 'type'>>
    inactive: boolean
    editable?: boolean
    /** Parent node, passed through to PlainText for analyzing content elements for key modification warnings */
    node?: DataNode
}

export const EmptyPlainTextContainer = memo<EmptyPlainTextContainerProps>(
    ({ element, inactive, editable = true, node }) => {
        if (!element?.id) return null

        const {
            paragraphTypography,
            inactiveText,
            addedText,
            removedText,
        } = useTreeViewStyles()

        const {
            plainTextWrapperContainer,
            plainTextContent,
            clickOverlay,
        } = usePlainTextStyles()
        const areTrackChangesVisible = useShallowEditorActionBar(
            (state) => state.visibilitySettingValues.areTrackChangesVisible
        )

        const plainTextRef = useRef<HTMLDivElement>(null)
        const changeType = element.changeType?.toUpperCase() ?? ''

        // Memoize normalized content to prevent unnecessary re-computations
        const normalizedContent = useMemo(() => {
            try {
                return normalizeTextContent(element)
            } catch (error) {
                console.error('Error normalizing text content:', error)
                // Return a safe default
                return { id: element.id, text: '', type: element.type }
            }
        }, [element.id, element.text, element.type, element.changeType])

        const statusClass = getStatusClass(
            inactive,
            changeType,
            areTrackChangesVisible,
            {
                inactiveText,
                addedText,
                removedText,
            }
        )

        const shouldShowOverlay = useMemo(
            () => editable && normalizedContent.text === '',
            [normalizedContent, editable]
        )

        // Handle overlay click to focus the PlainText component
        const handleOverlayClick = useCallback((e: React.MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()

            const wrapper = plainTextRef.current
            if (!wrapper) return

            // Inline DOM queries - single use, no need for utility function
            const plainTextElement = wrapper.querySelector(
                '[data-testid^="plain-text-"]:not([data-testid*="overlay"])'
            ) as HTMLElement | null

            const editableElement = wrapper.querySelector(
                '.ck-editor__editable, [contenteditable="true"]'
            ) as HTMLElement | null

            if (plainTextElement) {
                // Hide balloon panels before focusing
                hideInLineTrackTip()

                // Simple click and focus
                plainTextElement.click()
                plainTextElement.focus()

                // Use requestAnimationFrame for CKEditor activation timing
                requestAnimationFrame(() => {
                    editableElement?.focus()
                })
            }
        }, [])

        // Handle keyboard activation for accessibility
        const handleOverlayKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOverlayClick(e as any)
                }
            },
            [handleOverlayClick]
        )

        return (
            <div
                className={plainTextWrapperContainer}
                data-element-id={element.id}
                role={editable ? 'textbox' : undefined}
                ref={plainTextRef}
            >
                {/* Accessible overlay div for better click area */}
                {shouldShowOverlay && (
                    <div
                        className={clickOverlay}
                        onClick={handleOverlayClick}
                        onKeyDown={handleOverlayKeyDown}
                        role="button"
                        tabIndex={0}
                        aria-label="Click to edit empty text"
                        data-testid={`empty-text-overlay-${element.id}`}
                    />
                )}
                <div className={plainTextContent}>
                    <PlainText
                        contentItem={normalizedContent}
                        node={node}
                        editable={editable}
                        classes={clsx(paragraphTypography, statusClass)}
                    />
                </div>
            </div>
        )
    }
)
